import * as React from "react";
import styled, { css } from "styled-components";

import { createEntitiesConsumer } from "../../lib/entities";
import Hover from "../../common/components/Hover";
import { CompanyValueFeature } from "../entities";
import CompanyValuesDragSource from "./CompanyValuesDragSource";
import CompanyValuesDropTarget, { DropHandler } from "./CompanyValuesDropTarget";
import Icon from "../../common/components/Icon";

interface Props {
  feature: EntityId;
  index: number;
  isDragging: boolean;
  onDrop: DropHandler;
  onDragStart(): void;
  onDragEnd(): void;
  onRemove(feaure: EntityId): void;
}

const FeatureEntitiesConsumer = createEntitiesConsumer<{ feature: CompanyValueFeature }>({
  feature: "company_value_features",
});

class CompanyValuesDraggableFeature extends React.PureComponent<Props> {
  public render() {
    return (
      <FeatureEntitiesConsumer query={{ feature: this.props.feature }}>
        {this.renderWithFeature}
      </FeatureEntitiesConsumer>
    );
  }

  private renderWithFeature: typeof FeatureEntitiesConsumer.children = ({ feature }) => {
    const { index, isDragging, onDragStart, onDragEnd, onDrop, onRemove } = this.props;
    if (!feature) {
      return null;
    }
    return (
      <Wrapper>
        <CompanyValuesDragSource
          key={feature.feature}
          feature={feature.feature}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <Hover>
            {({ isHovered, ref }) => (
              <Item innerRef={ref} isHovered={isHovered}>
                <HandleWrapper isVisible={isHovered}>
                  <Handle name="wt-icon-bers-o-1" />
                </HandleWrapper>
                <Index>{index}</Index>
                <FeatureTitle>{feature.feature_title}</FeatureTitle>
                {isHovered && <RemoveIcon name="wt-icon-close-circle-o-1" onClick={() => onRemove(feature.feature)} />}
                {isDragging && (
                  <>
                    <CompanyValuesDropTarget feature={feature.feature} position={"before"} onDrop={onDrop} />
                    <CompanyValuesDropTarget feature={feature.feature} position={"after"} onDrop={onDrop} />
                  </>
                )}
              </Item>
            )}
          </Hover>
        </CompanyValuesDragSource>
      </Wrapper>
    );
  };
}

export const CompanyValuesDraggableFeaturePlaceholder: React.SFC = () => {
  return (
    <Placeholder>
      <PlaceholderIndex />
    </Placeholder>
  );
};

interface StyleProps {
  isHovered: boolean;
}

const Wrapper = styled.div`
  width: 100%;
  padding: 2px 0;
  box-sizing: border-box;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.36;
`;

const itemStyle = css`
  position: relative;
  padding: 7px 0;
  display: flex;
  font-size: 13px;
  line-height: 1.36;
  align-items: center;
`;

const Item = styled.div`
  ${itemStyle};
  ${(props: StyleProps) => (props.isHovered ? css`` : "")};
`;

const Placeholder = Wrapper.extend`
  ${itemStyle};
  padding: 9px 0 9px 22px;
`;

const FeatureTitle = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.56);
`;

const Index = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-right: 10px;
  line-height: 20px;
  font-weight: 400;
  color: white;
  border-radius: 50%;
  text-align: center;
  vertical-align: middle;
  box-sizing: border-box;
  background-color: rgba(33, 189, 219, 1);
`;

const PlaceholderIndex = Index.extend`
  background-color: transparent;
  border: 1px solid rgba(0, 0, 0, 0.27);
`;

const Handle = styled(Icon)`
  font-size: 14px;
  line-height: 1.36;
  color: rgba(0, 0, 0, 0.27);
  cursor: move;
`;

const RemoveIcon = styled(Icon)`
  position: absolute;
  right: 0;
  top: 0;
  width: 12px;
  height: 12px;
  padding: 9px;
  color: rgba(0, 0, 0, 0.27);
  line-height: 1;
  font-size: 13px;
  cursor: pointer;
`;

const HandleWrapper = styled.div`
  width: 16px;
  flex-shrink: 0;
  margin: 0 6px 0 0px;
  ${(p: { isVisible: boolean }) =>
    p.isVisible
      ? css`
          visibility: visible;
        `
      : css`
          visibility: hidden;
        `};
`;

export default CompanyValuesDraggableFeature;



// WEBPACK FOOTER //
// ./frontend/src/company_values/components/CompanyValuesDraggableFeature.tsx